import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useSetRecoilState } from 'recoil';
import { assignmentItemsDataState } from '../../assignment/store/assignment.state';
import { DataTableState } from '../../common/types';
import { itemSetItemsDataState } from '../../itemSet/store/itemSet.state';
import { actualityHeatmapTableDataState } from '../store/actuality.heatmap.state';
import {
  actualityHqCongestionsTableDataState,
  actualityHqTableDataState,
} from '../store/actuality.hq.state';
import { actualityJobsTableDataState } from '../store/actuality.jobs.state';
import { actualityKpiMetricsTableDataState } from '../store/actuality.kpiMetrics.state';
import {
  ActualityDataSetTableType,
  ActualityEntityType,
} from '../store/actuality.types';

export type ActualityDatasetTableDescriptor = {
  id: ActualityDataSetTableType;
  title: string;
  shortTitle: string;
  setFilter: (tableState: DataTableState<string>) => void;
  entities: Set<ActualityEntityType>;
};

const useActualityDatasetTableTypes = (): Record<
  ActualityDataSetTableType,
  ActualityDatasetTableDescriptor
> => {
  const { t } = useTranslation('feed');

  const setActivityMetricTableState = useSetRecoilState(
    actualityHeatmapTableDataState,
  );
  const setActivityFeedTableState = useSetRecoilState(
    actualityHqTableDataState,
  );
  const setJobsTableState = useSetRecoilState(actualityJobsTableDataState);
  const setCongestionsTableState = useSetRecoilState(
    actualityHqCongestionsTableDataState,
  );
  const setKpiMetricsTableState = useSetRecoilState(
    actualityKpiMetricsTableDataState,
  );
  const setItemSetTableState = useSetRecoilState(itemSetItemsDataState);
  const setAssignmentTableState = useSetRecoilState(assignmentItemsDataState);

  return {
    'activity-feed': {
      id: 'activity-feed',
      title: t`Activity Data`,
      shortTitle: t`Events`,
      setFilter: v => setActivityFeedTableState(v),
      entities: new Set<ActualityEntityType>([
        'event',
        'item',
        'location',
        'job',
        'agentId',
      ]),
    },

    'item-set': {
      id: 'item-set',
      title: t`Item Set`,
      shortTitle: t`Items`,
      setFilter: v => setItemSetTableState(v),
      entities: new Set<ActualityEntityType>(['item']),
    },

    assignment: {
      id: 'assignment',
      title: t`Assignment`,
      shortTitle: t`Assignment`,
      setFilter: v => setAssignmentTableState({ totalCount: 0, ...v }),
      entities: new Set<ActualityEntityType>(['item', 'location']),
    },

    layout: {
      id: 'layout',
      title: t`Layout`,
      shortTitle: t`Layout`,
      setFilter: _.noop,
      entities: new Set<ActualityEntityType>(['location']),
    },

    'activity-metric': {
      id: 'activity-metric',
      title: t`Data Stats`,
      shortTitle: t`Stats`,
      setFilter: v => setActivityMetricTableState(v),
      entities: new Set<ActualityEntityType>(['item', 'location']),
    },

    jobs: {
      id: 'jobs',
      title: t`Analysed Jobs`,
      shortTitle: t`Jobs`,
      setFilter: v => setJobsTableState(v),
      entities: new Set<ActualityEntityType>(['job', 'agentId']),
    },

    'congested-events': {
      id: 'congested-events',
      title: t`Congestions`,
      shortTitle: t`Congestions`,
      setFilter: v => setCongestionsTableState(v),
      entities: new Set<ActualityEntityType>(['event', 'job', 'agentId']),
    },

    'activity-plan': {
      id: 'activity-plan',
      title: t`Activity Plan`,
      shortTitle: t`Plan`,
      setFilter: _.noop,
      entities: new Set<ActualityEntityType>(['item', 'location']),
    },

    inventory: {
      id: 'inventory',
      title: t`Inventory`,
      shortTitle: t`Inventory`,
      setFilter: _.noop,
      entities: new Set<ActualityEntityType>(['item', 'location']),
    },

    'order-set': {
      id: 'order-set',
      title: t`Order Set`,
      shortTitle: t`Order`,
      setFilter: _.noop,
      entities: new Set<ActualityEntityType>(['order']),
    },

    'kpi-dashboard': {
      id: 'kpi-dashboard',
      title: t`KPI Dashboard`,
      shortTitle: t`KPI`,
      setFilter: _.noop,
      entities: new Set<ActualityEntityType>(['item', 'location']),
    },

    'kpi-metrics': {
      id: 'kpi-metrics',
      title: t`KPI Metrics Table`,
      shortTitle: t`Metrics`,
      setFilter: v => setKpiMetricsTableState(v),
      entities: new Set<ActualityEntityType>(['item', 'location']),
    },
  };
};

export default useActualityDatasetTableTypes;
